import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import SEO from "@/components/SEO";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import {
    RadioGroup,
    RadioGroupItem,
} from "@/components/ui/radio-group";

import { Label } from "@/components/ui/label";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

import {
    Banknote,
    CreditCard,
    Truck,
    Download,
    FileText,
    CheckCircle2,
} from "lucide-react";

export default function Checkout () {

    const { items, subtotal, clear } = useCart();
    const { t, lang } = useI18n();
    const nav = useNavigate();

    const [locations, setLocations] = useState({});
    const [libraries, setLibraries] = useState([]);

    // =========================
    // INVOICE MODAL STATES
    // =========================
    const [invoiceModal, setInvoiceModal] = useState(false);
    const [invoiceUrl, setInvoiceUrl] = useState("");
    const [orderNumber, setOrderNumber] = useState("");

    const [form, setForm] = useState({
        customer_name: "",
        phone: "",
        email: "",
        division: "",
        zilla: "",
        library_id: "",
        address: "",
        note: "",

        delivery_zone: "outside_dhaka",

        payment_method: "cod",
        bank_txn_id: "",
    });

    const [busy, setBusy] = useState(false);
    const [publicSettings, setPublicSettings] = useState({});
    const [orderPlaced, setOrderPlaced] = useState(false);


    // =========================
    // LOAD LOCATIONS
    // =========================
    useEffect(() => {

        api.get("/locations")
            .then((r) => setLocations(r.data));

        api.get("/settings/public")
            .then((r) => setPublicSettings(r.data));

    }, []);

    // =========================
    // LOAD LIBRARIES
    // =========================
    useEffect(() => {

        if (form.zilla) {

            api.get(
                `/libraries?zilla=${ encodeURIComponent(form.zilla) }`
            ).then((r) => setLibraries(r.data));

        } else {

            setLibraries([]);

        }

    }, [form.zilla]);

    // =========================
    // EMPTY CART CHECK
    // =========================
    useEffect(() => {

        if (items.length === 0 && !invoiceModal && !orderPlaced) {
            nav("/cart");
        }

    }, [items, nav, invoiceModal, orderPlaced]);

    const onChange = (k, v) =>
        setForm((f) => ({
            ...f,
            [k]: v,
        }));

    // =========================
    // DELIVERY FEE
    // =========================
    const insideDhakaCharge = publicSettings.delivery_charge_inside_dhaka ?? 60;
    const outsideDhakaCharge = publicSettings.delivery_charge_outside_dhaka ?? 120;
    const deliveryFee =
        form.delivery_zone === "inside_dhaka"
            ? insideDhakaCharge
            : outsideDhakaCharge;

    // =========================
    // TOTAL
    // =========================
    const total = subtotal + deliveryFee;

    // =========================
    // SUBMIT ORDER
    // =========================
    const submit = async (e) => {
        e.preventDefault();

        if (!form.division || !form.zilla) {
            return toast.error(
                lang === "bn"
                    ? "বিভাগ ও জেলা সিলেক্ট করুন"
                    : "Please select division and zilla"
            );
        }

        if (form.phone.length < 11 || form.phone.length > 15) {
            return toast.error(
                lang === "bn"
                    ? "মোবাইল নম্বর ১১ থেকে ১৫ সংখ্যার হতে হবে"
                    : "Phone number must be between 11 and 15 digits."
            );
        }

        setBusy(true);

        try {

            const { data } = await api.post("/orders", {
                ...form,
                items,
            });

            setOrderPlaced(true); // ✅ FIX

            setInvoiceUrl(
                data.invoice_url ||
                `/invoice/${ data.order_number }`
            );

            setOrderNumber(data.order_number);

            setInvoiceModal(true);

            setTimeout(() => {
                clear();
            }, 300);

        } catch (err) {

            toast.error(formatApiError(err));

        } finally {

            setBusy(false);

        }
    };

    return (
        <>
            <SEO title={t("checkout")} />

            {/* =========================
                PAGE
            ========================= */}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

                {/* TITLE */}
                <h1 className="text-3xl font-bold text-brand-navy">
                    {t("checkout")}
                </h1>

                <form
                    onSubmit={submit}
                    className="grid lg:grid-cols-3 gap-8 mt-8"
                >

                    {/* =========================
                        LEFT SIDE
                    ========================= */}

                    <div className="lg:col-span-2 space-y-6">

                        {/* =========================
                            ADDRESS SECTION
                        ========================= */}

                        <div className="bg-white border border-brand-light rounded-2xl p-6 space-y-4 shadow-sm">

                            <h3 className="font-bold text-brand-navy text-lg">
                                {t("delivery_address")}
                            </h3>

                            <div className="grid sm:grid-cols-2 gap-4">

                                {/* NAME */}
                                <Field
                                    label={t("full_name")}
                                    required
                                    value={form.customer_name}
                                    onChange={(v) =>
                                        onChange("customer_name", v)
                                    }
                                    testid="checkout-name"
                                />

                                {/* PHONE */}
                                <Field
                                    type="tel"
                                    label={t("phone")}
                                    required
                                    value={form.phone}
                                    onChange={(v) => onChange("phone", v.replace(/\D/g, ""))}
                                    minLength={11}
                                    maxLength={15}
                                    testid="checkout-phone"
                                />

                                {/* EMAIL */}
                                <Field
                                    type="email"
                                    label={t("email")}
                                    value={form.email}
                                    onChange={(v) =>
                                        onChange("email", v)
                                    }
                                    testid="checkout-email"
                                />

                                {/* DIVISION */}
                                <div>

                                    <label className="block text-sm font-medium mb-1.5 text-brand-navy">
                                        {t("select_division")} *
                                    </label>

                                    <Select
                                        value={form.division}
                                        onValueChange={(v) => {

                                            onChange("division", v);
                                            onChange("zilla", "");

                                        }}
                                    >

                                        <SelectTrigger
                                            data-testid="division-select"
                                            className="rounded-xl"
                                        >

                                            <SelectValue
                                                placeholder={t("select_division")}
                                            />

                                        </SelectTrigger>

                                        <SelectContent className="font-bn">

                                            {Object.keys(locations).map((d) => (

                                                <SelectItem
                                                    key={d}
                                                    value={d}
                                                >
                                                    {d}
                                                </SelectItem>

                                            ))}

                                        </SelectContent>

                                    </Select>

                                </div>

                                {/* ZILLA */}
                                <div>

                                    <label className="block text-sm font-medium mb-1.5 text-brand-navy">
                                        {t("select_zilla")} *
                                    </label>

                                    <Select
                                        value={form.zilla}
                                        onValueChange={(v) => {

                                            onChange("zilla", v);
                                            onChange("library_id", "");

                                        }}
                                        disabled={!form.division}
                                    >

                                        <SelectTrigger
                                            data-testid="zilla-select"
                                            className="rounded-xl"
                                        >

                                            <SelectValue
                                                placeholder={t("select_zilla")}
                                            />

                                        </SelectTrigger>

                                        <SelectContent className="font-bn">

                                            {(locations[form.division] || []).map((z) => (

                                                <SelectItem
                                                    key={z}
                                                    value={z}
                                                >
                                                    {z}
                                                </SelectItem>

                                            ))}

                                        </SelectContent>

                                    </Select>

                                </div>

                            </div>

                            {/* ADDRESS */}
                            <Field
                                label={t("address")}
                                required
                                value={form.address}
                                onChange={(v) =>
                                    onChange("address", v)
                                }
                                testid="checkout-address"
                            />

                            {/* =========================
                                DELIVERY ZONE
                            ========================= */}

                            <div>

                                <label className="block text-sm font-medium mb-2 text-brand-navy">
                                    Delivery Area *
                                </label>

                                <RadioGroup
                                    value={form.delivery_zone}
                                    onValueChange={(v) =>
                                        onChange("delivery_zone", v)
                                    }
                                    className="grid sm:grid-cols-2 gap-3"
                                >

                                    {/* INSIDE DHAKA */}
                                    <Label
                                        className={`p-4 border rounded-2xl cursor-pointer flex items-start gap-3 transition-all duration-200 ${ form.delivery_zone === "inside_dhaka"
                                            ? "border-brand-royal bg-brand-light/20 shadow-sm"
                                            : "border-brand-light hover:border-brand-royal/40"
                                            }`}
                                    >

                                        <RadioGroupItem value="inside_dhaka" />

                                        <div>

                                            <div className="flex items-center gap-2 font-semibold text-brand-navy">

                                                <Truck className="w-4 h-4" />

                                                Inside Dhaka

                                            </div>

                                            <div className="text-xs text-brand-navy/60 mt-1">
                                                Delivery Fee: ৳{insideDhakaCharge}
                                            </div>

                                        </div>

                                    </Label>

                                    {/* OUTSIDE DHAKA */}
                                    <Label
                                        className={`p-4 border rounded-2xl cursor-pointer flex items-start gap-3 transition-all duration-200 ${ form.delivery_zone === "outside_dhaka"
                                            ? "border-brand-royal bg-brand-light/20 shadow-sm"
                                            : "border-brand-light hover:border-brand-royal/40"
                                            }`}
                                    >

                                        <RadioGroupItem value="outside_dhaka" />

                                        <div>

                                            <div className="flex items-center gap-2 font-semibold text-brand-navy">

                                                <Truck className="w-4 h-4" />

                                                Outside Dhaka

                                            </div>

                                            <div className="text-xs text-brand-navy/60 mt-1">
                                                Delivery Fee: ৳{outsideDhakaCharge}
                                            </div>

                                        </div>

                                    </Label>

                                </RadioGroup>

                            </div>

                            {/* NOTE */}
                            <Field
                                label={t("order_note")}
                                value={form.note}
                                onChange={(v) =>
                                    onChange("note", v)
                                }
                                testid="checkout-note"
                            />

                        </div>

                        {/* =========================
                            PAYMENT SECTION
                        ========================= */}

                        <div className="bg-white border border-brand-light rounded-2xl p-6 shadow-sm">

                            <h3 className="font-bold text-brand-navy text-lg">
                                {t("payment_method")}
                            </h3>

                            <RadioGroup
                                className="mt-4 grid sm:grid-cols-2 gap-3"
                                value={form.payment_method}
                                onValueChange={(v) =>
                                    onChange("payment_method", v)
                                }
                            >

                                {/* COD */}
                                <Label
                                    className={`p-4 border rounded-2xl cursor-pointer flex items-start gap-3 transition-all duration-200 ${ form.payment_method === "cod"
                                        ? "border-brand-royal bg-brand-light/20 shadow-sm"
                                        : "border-brand-light hover:border-brand-royal/40"
                                        }`}
                                >

                                    <RadioGroupItem
                                        value="cod"
                                        data-testid="payment-cod-radio"
                                    />

                                    <div>

                                        <div className="flex items-center gap-2 font-semibold text-brand-navy">

                                            <Banknote className="w-4 h-4" />

                                            {t("payment_cod")}

                                        </div>

                                        <div className="text-xs text-brand-navy/60 mt-1">

                                            {lang === "bn"
                                                ? "পণ্য হাতে পেয়ে টাকা পরিশোধ করুন"
                                                : "Pay when you receive"}

                                        </div>

                                    </div>

                                </Label>

                            </RadioGroup>

                        </div>

                    </div>

                    {/* =========================
                        RIGHT SIDE
                    ========================= */}

                    <aside className="lg:col-span-1">

                        <div className="glass rounded-2xl p-6 sticky top-32 border border-brand-light shadow-sm">

                            <h3 className="font-bold text-brand-navy text-lg">
                                {t("order_summary")}
                            </h3>

                            <div className="mt-3 space-y-2 max-h-60 overflow-y-auto pr-1">

                                {items.map((i) => (

                                    <div
                                        key={i.book_id}
                                        className="flex items-center gap-2 text-xs bg-white rounded-xl p-2 border border-brand-light"
                                    >

                                        <img
                                            src={i.cover_image}
                                            alt=""
                                            className="w-10 h-12 rounded object-cover"
                                        />

                                        <div className="flex-1 min-w-0 truncate">
                                            {i.title} × {i.quantity}
                                        </div>

                                        <div className="font-semibold">
                                            ৳{i.price * i.quantity}
                                        </div>

                                    </div>

                                ))}

                            </div>

                            <div className="border-t border-brand-light mt-4 pt-4 space-y-2 text-sm">

                                <div className="flex justify-between">
                                    <span>{t("subtotal")}</span>
                                    <span>৳{subtotal}</span>
                                </div>

                                <div className="flex justify-between">
                                    <span>{t("delivery_fee")}</span>
                                    <span>৳{deliveryFee}</span>
                                </div>

                                <div className="flex justify-between font-bold text-base text-brand-royal pt-2 border-t border-brand-light">

                                    <span>{t("total")}</span>

                                    <span>৳{total}</span>

                                </div>

                            </div>

                            {/* SUBMIT BUTTON */}
                            <button
                                disabled={busy}
                                className="btn-accent w-full mt-6 py-3 rounded-2xl font-semibold disabled:opacity-60 transition-all duration-200 shadow-lg"
                                data-testid="checkout-submit"
                            >

                                {busy
                                    ? "Processing..."
                                    : t("confirm_order")}

                            </button>

                        </div>

                    </aside>

                </form>

            </div>

            {/* =========================
                INVOICE MODAL
            ========================= */}

            <Dialog
                open={invoiceModal}
                onOpenChange={(open) => {

                    setInvoiceModal(open);

                    // CLOSE HOLE SUCCESS PAGE E JABE
                    if (!open) {

                        nav(`/order-success/${ orderNumber }`);

                    }

                }}
            >

                <DialogContent className="sm:max-w-md rounded-[32px] border-0 p-0 overflow-hidden shadow-2xl">

                    {/* TOP */}
                    <div className="bg-gradient-to-br from-brand-royal via-brand-royal to-brand-navy p-8 text-white text-center relative overflow-hidden">

                        <div className="absolute inset-0 opacity-10">

                            <div className="absolute -top-10 -left-10 w-40 h-40 bg-white rounded-full" />

                            <div className="absolute bottom-0 right-0 w-32 h-32 bg-white rounded-full" />

                        </div>

                        <div className="relative z-10">

                            <div className="w-24 h-24 mx-auto rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mb-5 border border-white/20 shadow-lg">

                                <CheckCircle2 className="w-12 h-12 text-white" />

                            </div>

                            <DialogHeader>

                                <DialogTitle className="text-3xl font-bold text-center text-white tracking-tight">

                                    Order Submitted

                                </DialogTitle>

                                <DialogDescription className="text-white/80 text-sm mt-2 text-center leading-relaxed">

                                    {lang === "bn"
                                        ? "আপনার অর্ডার সফলভাবে গৃহীত হয়েছে খুব শীঘ্রই আমাদের প্রতিনিধি আপনাকে ফোন কলের মাধ্যমে অর্ডারটি কনফার্ম করবে।"
                                        : "Your order has been successfully received. Our representative will confirm the order via phone call shortly."}

                                </DialogDescription>

                            </DialogHeader>

                        </div>

                    </div>

                    {/* BODY */}
                    <div className="p-4 bg-white">

                        {/* ORDER NUMBER */}
                        <div className="bg-brand-light/20 border border-brand-light rounded-2xl p-5 text-center">

                            <div className="text-sm text-brand-navy/60">
                                Order Number
                            </div>

                            <div className="text-2xl font-bold text-brand-navy mt-1 tracking-wide">
                                #{orderNumber}
                            </div>

                        </div>

                        {/* INVOICE READY */}
                        <div className="mt-4 flex items-start gap-4 bg-blue-50 border border-blue-100 rounded-2xl p-4">

                            <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center shrink-0">

                                <FileText className="w-5 h-5 text-blue-700" />

                            </div>

                            <div>

                                <h4 className="font-semibold text-brand-navy text-base">
                                    Invoice Ready
                                </h4>

                                <p className="text-sm text-brand-navy/60 mt-1 leading-relaxed">

                                    {lang === "bn"
                                        ? "আপনার ইনভয়েস এখনই ডাউনলোড করতে পারবেন"
                                        : "Download your invoice instantly"}

                                </p>

                            </div>

                        </div>

                        {/* BUTTONS */}
                        <div className="mt-4 space-y-3">

                            {/* DOWNLOAD */}
                            <Link
                                to={invoiceUrl}
                                download
                                target="_blank"
                                rel="noreferrer"
                                className="w-full h-12 rounded-2xl bg-brand-royal hover:bg-brand-navy transition-all duration-200 text-white font-semibold flex items-center justify-center gap-2 shadow-lg hover:scale-[1.01]"
                            >

                                <Download className="w-5 h-5" />

                                {lang === "bn"
                                    ? "ইনভয়েস ডাউনলোড করুন"
                                    : "Download Invoice"}

                            </Link>

                            {/* SUCCESS PAGE */}
                            <button
                                type="button"
                                onClick={() => {

                                    setInvoiceModal(false);

                                    setTimeout(() => {

                                        nav(`/order-success/${ orderNumber }`);

                                    }, 200);

                                }}
                                className="w-full h-12 rounded-2xl border border-brand-light bg-white hover:bg-brand-light/20 transition-all duration-200 font-semibold text-brand-navy flex items-center justify-center"
                            >

                                {lang === "bn"
                                    ? "অর্ডার ডিটেইলস দেখুন"
                                    : "View Order Details"}

                            </button>

                        </div>

                    </div>

                </DialogContent>

            </Dialog>

        </>
    );
}

// =========================
// FIELD COMPONENT
// =========================

function Field ({
    label,
    value,
    onChange,
    type = "text",
    required,
    testid,
    minLength,
    maxLength,
}) {

    return (

        <div>

            <label className="block text-sm font-medium mb-1.5 text-brand-navy">

                {label}

                {required && " *"}

            </label>

            <input
                type={type}
                value={value}
                required={required}
                minLength={minLength}
                maxLength={maxLength}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full px-4 py-3 rounded-2xl border border-brand-light bg-white focus:outline-none focus:ring-2 focus:ring-brand-royal/30 transition-all duration-200"
            />

        </div>

    );
}